import React, { useState } from 'react';
import { ANDROID_PLAY_URL, WINDOWS_INSTALLER_URL } from './installLinks';

/**
 * How a restaurant reaches a person here.
 *
 * Empty until someone fills it in, and every line below renders only when
 * it has a value — an invented support address on a live page sends people
 * into a mailbox nobody reads, which is worse than offering no address at
 * all. With all four empty the whole Contact section and its menu links
 * disappear, so nothing half-finished can ship by accident.
 *
 * `whatsapp` is digits only, country code first and no +, because that is
 * what wa.me takes: '919866951533'.
 */
const CONTACT = {
  email: 'info@scandeer.com',
  /** Every number worth ringing. Each becomes its own tel: link. */
  phones: ['+91 78159 67855', '+91 99662 26567'],
  whatsapp: '917815967855',
  address:
    'SCANDEER INDIA PVT. LTD., D.No. 12-10-2 B, 1st Floor, Convent Street, ' +
    'Adj to Punjab National Bank, Tarapet, Vijayawada - 520001, Andhra Pradesh',
};

const HAS_CONTACT = Object.values(CONTACT).some((v) =>
  Array.isArray(v) ? v.length > 0 : v.trim() !== '',
);

export function LandingPage({
  onLogin,
  signedIn = false,
}: {
  onLogin: () => void;
  /** An owner who still has a session lands here from the header's Website
   *  link, and "Log In" reads as nonsense to someone already logged in. */
  signedIn?: boolean;
}) {
  const [activeTable, setActiveTable] = useState<'T-1' | 'T-2' | 'T-3' | 'Bar-1'>('T-1');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');

  const sampleBills: Record<string, {
    tableName: string;
    area: string;
    server: string;
    items: { name: string; qty: number; price: number }[];
    status: 'occupied' | 'billing' | 'vacant';
  }> = {
    'T-1': {
      tableName: 'Table 01',
      area: 'AC Dining',
      server: 'Rahul',
      status: 'occupied',
      items: [
        { name: 'Paneer Butter Masala', qty: 2, price: 280 },
        { name: 'Butter Naan', qty: 4, price: 45 },
        { name: 'Jeera Rice', qty: 1, price: 180 },
        { name: 'Fresh Lime Soda', qty: 2, price: 70 },
      ],
    },
    'T-2': {
      tableName: 'Table 02',
      area: 'Main Hall',
      server: 'Kiran',
      status: 'billing',
      items: [
        { name: 'Chicken Biryani Special', qty: 2, price: 340 },
        { name: 'Mutton Seekh Kebab', qty: 1, price: 390 },
        { name: 'Virgin Mojito', qty: 2, price: 120 },
      ],
    },
    'T-3': {
      tableName: 'Table 03',
      area: 'Balcony',
      server: 'Ganesh',
      status: 'occupied',
      items: [
        { name: 'Crispy Corn Salt & Pepper', qty: 1, price: 210 },
        { name: 'Veg Hakka Noodles', qty: 1, price: 220 },
        { name: 'Chilli Chicken Gravy', qty: 1, price: 290 },
      ],
    },
    'Bar-1': {
      tableName: 'Bar Counter 01',
      area: 'Lounge Bar',
      server: 'Suresh',
      status: 'billing',
      items: [
        { name: 'Signature Long Island Iced Tea', qty: 2, price: 450 },
        { name: 'Loaded Nachos Grande', qty: 1, price: 260 },
      ],
    },
  };

  const currentBill = sampleBills[activeTable];
  const subtotal = currentBill.items.reduce((sum, item) => sum + item.qty * item.price, 0);
  const gst = Math.round(subtotal * 0.05);
  const total = subtotal + gst;

  return (
    <div className="landing-root">
      {/* Navigation Header */}
      <header className="landing-nav">
        <div className="landing-nav-inner">
          <div className="landing-brand">
            <img className="brand-icon" src="/favicon-180.png" alt="" width={28} height={28} />
            <strong>BILLDEDO</strong>
            <span className="brand-tag">RESTAURANT POS</span>
          </div>

          <nav className="landing-menu">
            <a href="#features">Features</a>
            <a href="#private-cloud">Your Data</a>
            <a href={WINDOWS_INSTALLER_URL}>Windows POS</a>
            <a href={ANDROID_PLAY_URL} target="_blank" rel="noreferrer">
              Android POS
            </a>
            <a href="#pricing">Pricing</a>
            <a href="#demo">See It Live</a>
            <a href="#comparison">Why Us?</a>
            {/* Served from this portal's own assets - see portal/web/public. */}
            <a href="/setup-guide.pdf" target="_blank" rel="noreferrer">Setup Guide</a>
            {HAS_CONTACT && <a href="#contact">Contact</a>}
          </nav>

          <div className="landing-actions">
            <button className="btn primary glow" onClick={onLogin}>
              {signedIn ? 'Go to Console' : 'Log In'} →
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="landing-hero">
        <div className="hero-glow-blob" />
        <div className="hero-glow-blob-2" />

        <div className="landing-container hero-content">
          <div className="hero-badge">
            <span className="pulse-dot" />
            <span>Billing software your restaurant actually owns · 7-day free trial</span>
          </div>

          <h1 className="hero-title">
            Everything Your Restaurant Runs On, <span className="text-gradient">In One Place</span>.
          </h1>

          <p className="hero-subtitle">
            Billing counter, live table plan, kitchen screens and self-order menus — working together, updating the
            moment anything changes. It all stays in a private space of your own, so your customer list and your day's
            numbers belong to you and nobody else.
          </p>

          <div className="hero-cta-group">
            <button className="btn lg primary glow" onClick={onLogin}>
              {signedIn ? 'Go to Your Console' : 'Log In to Console'} →
            </button>
            <a href="#pricing" className="btn lg ghost">
              See Plans &amp; Pricing ↓
            </a>
          </div>

          <div className="hero-metrics">
            <div className="metric-pill">
              <strong>100% Yours</strong>
              <span>Data In Your Name</span>
            </div>
            <div className="metric-divider" />
            <div className="metric-pill">
              <strong>₹833 / mo</strong>
              <span>On The Annual Plan</span>
            </div>
            <div className="metric-divider" />
            <div className="metric-pill">
              <strong>7 Days</strong>
              <span>Free To Try</span>
            </div>
            <div className="metric-divider" />
            <div className="metric-pill">
              <strong>Bills Offline</strong>
              <span>Even If Internet Drops</span>
            </div>
          </div>
        </div>
      </section>

      {/* Data Ownership Section */}
      <section id="private-cloud" className="landing-section private-cloud-section">
        <div className="landing-container">
          <div className="private-cloud-banner">
            <div className="cloud-badge">🛡️ YOUR DATA, YOUR NAME</div>
            <h2>Your Restaurant's Numbers Stay Your Business</h2>
            <p className="cloud-subtitle">
              Most billing companies keep your customer list, your sales history and your costing on their own systems,
              and hand you a copy only when it suits them.
              <strong> With BILLDEDO, every restaurant gets a private setup that stays in your own account.</strong>
            </p>

            <div className="cloud-pillars-grid">
              <div className="cloud-pillar-card">
                <div className="cp-icon">🔒</div>
                <h4>Nothing Held Hostage</h4>
                <p>The account is in your name. Take a full copy of your data with you whenever you want.</p>
              </div>

              <div className="cloud-pillar-card">
                <div className="cp-icon">⚡</div>
                <h4>Quick On Every Screen</h4>
                <p>Bills open, save and print the moment you tap — no waiting around at a busy counter.</p>
              </div>

              <div className="cloud-pillar-card">
                <div className="cp-icon">🚫</div>
                <h4>Nobody Looking Over Your Shoulder</h4>
                <p>No outside company gets to see your sales, your prices, or what you earn on each dish.</p>
              </div>

              <div className="cloud-pillar-card">
                <div className="cp-icon">📈</div>
                <h4>Handles Your Busiest Night</h4>
                <p>Full halls, festival rushes and party bookings — it keeps up instead of slowing to a crawl.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="landing-section pricing-section">
        <div className="landing-container">
          <div className="section-head text-center">
            <span className="section-eyebrow">SIMPLE, ALL-INCLUSIVE PRICING</span>
            <h2>One Simple Plan for Every Restaurant</h2>
            <p>Everything is included in one price. No charge per counter, no cut of your sales, no lock-in.</p>

            <div className="billing-toggle-wrapper">
              <span className={billingCycle === 'monthly' ? 'active' : ''}>Monthly</span>
              <button
                className={`toggle-switch ${billingCycle === 'annual' ? 'on' : ''}`}
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
                aria-label="Toggle billing cycle"
              >
                <span className="toggle-handle" />
              </button>
              <span className={billingCycle === 'annual' ? 'active' : ''}>
                Annual <span className="discount-pill">2 Months Free</span>
              </span>
            </div>
          </div>

          <div className="pricing-cards-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24, maxWidth: 900, margin: '0 auto' }}>
            {/* Card 1: Monthly Plan (₹1,000 / month) */}
            <div className="pricing-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', border: '1px solid var(--line, rgba(255,255,255,0.1))', background: 'var(--card-bg, #1e293b)' }}>
              <div>
                <div style={{ background: 'rgba(56, 189, 248, 0.12)', color: '#38bdf8', padding: '6px 12px', borderRadius: '6px 6px 0 0', textAlign: 'center', fontSize: 12, fontWeight: 700, letterSpacing: '0.5px' }}>
                  🎁 7-DAY FREE TRIAL INCLUDED
                </div>
                <div className="pricing-card-header text-center" style={{ padding: '24px 20px 16px 20px' }}>
                  <h3 style={{ fontSize: 22, marginBottom: 6 }}>Monthly Plan</h3>
                  <p className="plan-desc" style={{ fontSize: 13, color: 'var(--text-2, #94a3b8)', marginBottom: 16 }}>
                    Pay month to month. Pause or stop whenever you like.
                  </p>
                  <div className="price-tag" style={{ justifyContent: 'center' }}>
                    <span className="currency">₹</span>
                    <span className="amount" style={{ fontSize: 44, fontWeight: 800 }}>1,000</span>
                    <span className="period" style={{ fontSize: 15 }}>/ month</span>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted, #64748b)' }}>Billed monthly. First 7 days free.</div>
                </div>

                <div className="pricing-features" style={{ padding: '16px 20px' }}>
                  <div className="feature-item highlight">✓ <strong>Fast Billing Counter</strong></div>
                  <div className="feature-item highlight">✓ <strong>Unlimited Captain App Phones</strong></div>
                  <div className="feature-item">✓ <strong>Live Table Plan For Every Hall</strong></div>
                  <div className="feature-item">✓ <strong>Paperless Kitchen Screens</strong></div>
                  <div className="feature-item">✓ <strong>Separate Kitchen &amp; Bar Print Slips</strong></div>
                  <div className="feature-item">✓ <strong>QR Menus &amp; Self-Order Counters</strong></div>
                  <div className="feature-item">✓ <strong>Your Own Private Setup</strong></div>
                </div>
              </div>

              <div style={{ padding: 20 }}>
                <button className="btn primary outline w-full" onClick={onLogin} style={{ padding: '12px 16px', fontWeight: 700 }}>
                  Start Monthly (₹1,000/mo) →
                </button>
              </div>
            </div>

            {/* Card 2: Annual Plan (₹10,000 / year) — Best Value */}
            <div className="pricing-card popular" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', border: '2px solid var(--accent, #38bdf8)', boxShadow: '0 12px 40px rgba(56, 189, 248, 0.18)' }}>
              <div>
                <div className="popular-badge" style={{ background: 'linear-gradient(135deg, #38bdf8, #818cf8)' }}>
                  ⭐ BEST VALUE — SAVE ₹2,000 / YEAR
                </div>
                <div className="pricing-card-header text-center" style={{ padding: '24px 20px 16px 20px' }}>
                  <h3 style={{ fontSize: 22, marginBottom: 6 }}>Annual Plan</h3>
                  <p className="plan-desc" style={{ fontSize: 13, color: 'var(--text-2, #94a3b8)', marginBottom: 16 }}>
                    The popular choice for established restaurants and cafes.
                  </p>
                  <div className="price-tag" style={{ justifyContent: 'center' }}>
                    <span className="currency">₹</span>
                    <span className="amount" style={{ fontSize: 44, fontWeight: 800 }}>10,000</span>
                    <span className="period" style={{ fontSize: 15 }}>/ year</span>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 13, color: '#38bdf8', fontWeight: 600 }}>
                    Works out to ₹833/mo (2 months free)
                  </div>
                </div>

                <div className="pricing-features" style={{ padding: '16px 20px' }}>
                  <div className="feature-item highlight">✓ <strong>Everything In The Monthly Plan</strong></div>
                  <div className="feature-item highlight">✓ <strong>Save ₹2,000 Every Year</strong></div>
                  <div className="feature-item">✓ <strong>Unlimited Captain App Phones</strong></div>
                  <div className="feature-item">✓ <strong>Paperless Kitchen Screens</strong></div>
                  <div className="feature-item">✓ <strong>Your Own Private Setup</strong></div>
                  <div className="feature-item">✓ <strong>Priority Support</strong></div>
                  <div className="feature-item">✓ <strong>🎁 7-Day Free Trial Included</strong></div>
                </div>
              </div>

              <div style={{ padding: 20 }}>
                <button className="btn primary glow w-full" onClick={onLogin} style={{ padding: '14px 16px', fontWeight: 700, fontSize: 15 }}>
                  🚀 Start Annual Plan (₹10,000/yr) →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Simulator Section */}
      <section id="demo" className="landing-section demo-section">
        <div className="landing-container">
          <div className="section-head text-center">
            <span className="section-eyebrow">TAKE A LOOK AROUND</span>
            <h2>See How A Busy Evening Looks</h2>
            <p>Tap any table to open its running bill, just the way your cashier would at the counter.</p>
          </div>

          <div className="simulator-grid">
            {/* Interactive Floor Plan */}
            <div className="sim-panel floor-panel">
              <div className="sim-panel-header">
                <h3>🪑 Table Plan</h3>
                <span className="status-badge-ok">Updating Live</span>
              </div>
              <div className="table-selector-grid">
                {Object.keys(sampleBills).map((key) => {
                  const b = sampleBills[key];
                  const isSelected = activeTable === key;
                  return (
                    <button
                      key={key}
                      className={`sim-table-card ${isSelected ? 'active' : ''} ${b.status}`}
                      onClick={() => setActiveTable(key as any)}
                    >
                      <div className="sim-table-num">{b.tableName}</div>
                      <div className="sim-table-meta">{b.area}</div>
                      <div className="sim-table-status">
                        <span className={`dot ${b.status}`} />
                        {b.status}
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="sim-helper-hint">
                💡 An order taken at the table reaches the billing counter and the kitchen straight away — nobody has to
                walk a slip across the floor.
              </div>
            </div>

            {/* Simulated Live Order & Receipt */}
            <div className="sim-panel receipt-panel">
              <div className="sim-panel-header">
                <h3>🧾 Running Bill · {currentBill.tableName}</h3>
                <span className="pill">{currentBill.area}</span>
              </div>

              <div className="receipt-mockup">
                <div className="receipt-header">
                  <strong>BILLDEDO RESTAURANT POS</strong>
                  <div className="hint mono">Served by {currentBill.server} · Order #4082</div>
                </div>

                <div className="receipt-items">
                  {currentBill.items.map((item, idx) => (
                    <div key={idx} className="receipt-row">
                      <span className="receipt-name">
                        {item.qty}x {item.name}
                      </span>
                      <span className="receipt-price mono">₹{item.qty * item.price}</span>
                    </div>
                  ))}
                </div>

                <div className="receipt-calc">
                  <div className="receipt-calc-row">
                    <span>Subtotal</span>
                    <span className="mono">₹{subtotal}</span>
                  </div>
                  <div className="receipt-calc-row">
                    <span>CGST + SGST (5%)</span>
                    <span className="mono">₹{gst}</span>
                  </div>
                  <div className="receipt-calc-row total">
                    <span>Grand Total</span>
                    <span className="mono">₹{total}</span>
                  </div>
                </div>

                <div className="receipt-actions">
                  <button className="btn sm primary" onClick={() => alert('Bill sent to the counter printer.')}>
                    🖨️ Print Bill
                  </button>
                  <button className="btn sm ghost" onClick={() => alert('Order sent to the kitchen screen.')}>
                    👨‍🍳 Send To Kitchen
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Feature Pillars */}
      <section id="features" className="landing-section features-section">
        <div className="landing-container">
          <div className="section-head text-center">
            <span className="section-eyebrow">BUILT FOR A FULL HOUSE</span>
            <h2>Everything Your Restaurant Needs</h2>
            <p>From fine dining to a busy takeaway counter, stay on top of every table and every bill.</p>
          </div>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🛡️</div>
              <h3>A Setup That's Yours</h3>
              <p>
                Your restaurant runs in its own private space, under your own account. Complete privacy, and nothing
                tying you down.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">📱</div>
              <h3>Captain App On Any Phone</h3>
              <p>
                Your captains take the order standing at the table, on the phone already in their pocket. No handheld
                machines to buy, and no charge per phone.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">🍽️</div>
              <h3>Live Table Plan</h3>
              <p>
                See every hall at a glance: who is seated, what has been served, which table is ready for the bill and
                which one is still waiting.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">📺</div>
              <h3>Paperless Kitchen Screens</h3>
              <p>
                Orders appear in front of your chefs in the order they came in, so nothing is missed and nothing gets
                cooked twice.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">📶</div>
              <h3>Keeps Billing Without Internet</h3>
              <p>
                If the connection drops mid-service, your counter carries on taking orders and printing bills. It all
                catches up on its own.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">🖨️</div>
              <h3>Kitchen &amp; Bar Slips, Sorted</h3>
              <p>
                Food goes to the kitchen printer and drinks to the bar automatically. Your staff never have to sort
                slips by hand.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Captain App Callout */}
      <section className="landing-section captain-callout-section">
        <div className="landing-container">
          <div className="callout-card">
            <div className="callout-content">
              <div className="callout-badge">📱 ORDERS TAKEN AT THE TABLE</div>
              <h2>Your Captains Never Walk Back To The Counter</h2>
              <p>
                A captain takes the order at the table on their own phone. It reaches the billing counter and the
                kitchen the moment they tap send — no slips carried across the floor, no order read out twice, no
                arguments later about who ordered what.
              </p>
              <p className="callout-note">
                Any captain on your restaurant's WiFi is signed straight into your restaurant — nothing to install and
                nothing to set up on each phone.
              </p>
            </div>
            <div className="callout-visual">
              <div className="phone-mockup">
                <div className="phone-screen">
                  <div className="phone-header">
                    <span>📶 Shop WiFi</span>
                    <span>10:30 AM</span>
                  </div>
                  <div className="phone-app-preview">
                    <div className="phone-pill">BILLDEDO CAPTAIN</div>
                    <div className="phone-restaurant-name">Amrutha Restaurant</div>
                    <div className="phone-tables-grid">
                      <div className="p-table occupied">T1</div>
                      <div className="p-table">T2</div>
                      <div className="p-table occupied">T3</div>
                      <div className="p-table">T4</div>
                    </div>
                    <div className="phone-btn">⚡ New Order</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section id="comparison" className="landing-section comparison-section">
        <div className="landing-container">
          <div className="section-head text-center">
            <span className="section-eyebrow">AN HONEST COMPARISON</span>
            <h2>Why Restaurants Choose BILLDEDO</h2>
          </div>

          <div className="comparison-table-wrapper">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th>What Matters</th>
                  <th className="highlight-col">⚡ BILLDEDO</th>
                  <th>Old Counter Software</th>
                  <th>Rented Online POS</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>What You Pay</td>
                  <td className="highlight-col text-ok font-bold">₹1,000 / month, 7 days free to try</td>
                  <td>Heavy one-time cost (₹30,000+)</td>
                  <td>₹3,000 – ₹10,000 / month plus a cut of your sales</td>
                </tr>
                <tr>
                  <td>Who Owns Your Data</td>
                  <td className="highlight-col text-ok font-bold">You do — it stays in your account</td>
                  <td>Stuck on one machine at the counter</td>
                  <td>Held by the company you rent from</td>
                </tr>
                <tr>
                  <td>Speed At The Counter</td>
                  <td className="highlight-col text-ok font-bold">Instant on every screen</td>
                  <td>Fine in the shop, nothing from outside</td>
                  <td>Slows down when their servers are busy</td>
                </tr>
                <tr>
                  <td>If The Internet Drops</td>
                  <td className="highlight-col text-ok font-bold">Keeps billing, catches up after</td>
                  <td>Keeps billing, but never syncs</td>
                  <td>Stops working until it is back</td>
                </tr>
                <tr>
                  <td>Captain App For Waiters</td>
                  <td className="highlight-col text-ok font-bold">Included, on phones your staff already carry</td>
                  <td>Needs costly handheld machines</td>
                  <td>Charged as an add-on for every device</td>
                </tr>
                <tr>
                  <td>Kitchen Screens</td>
                  <td className="highlight-col text-ok font-bold">Included in the plan</td>
                  <td>Needs special hardware you have to buy</td>
                  <td>Charged extra, every month</td>
                </tr>
                <tr>
                  <td>Getting Started</td>
                  <td className="highlight-col text-ok font-bold">Under a minute, on your own</td>
                  <td>Days of technician visits</td>
                  <td>Weeks of back-and-forth onboarding</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 3-Step Quickstart */}
      <section className="landing-section steps-section">
        <div className="landing-container">
          <div className="section-head text-center">
            <span className="section-eyebrow">GETTING STARTED</span>
            <h2>Open For Business In 3 Steps</h2>
          </div>

          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h3>Log In &amp; Start Your Free Trial</h3>
              <p>Sign in, add your restaurant, and lay out your halls and tables the way they really are.</p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <h3>Claim Your Private Space</h3>
              <p>Link your own free account in a couple of clicks — we walk you through every step.</p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <h3>Go Live</h3>
              <p>Everything is set up for you in the background. Print your first bill inside a minute.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA Banner */}
      <section className="landing-cta-banner">
        <div className="landing-container text-center">
          <h2>Ready To Take Charge Of Your Counter?</h2>
          <p>Try it free for 7 days — your tables, your menu and your numbers in one place from the very first bill.</p>
          <div className="cta-buttons">
            <button className="btn lg primary glow" onClick={onLogin}>
              {signedIn ? 'Go to Your Console' : 'Log In to Console'} →
            </button>
            {/*
              Beside the button that starts it, because the two permanent
              decisions in setting up a restaurant - the short name and whose
              Cloudflare account it goes into - are both made in the first
              five minutes, and both are explained in here.
            */}
            <a
              className="btn lg ghost"
              href="/setup-guide.pdf"
              target="_blank"
              rel="noreferrer"
            >
              Read the Setup Guide ↓
            </a>
          </div>
          {/* Where the till itself comes from, for the counter PC or a tablet. */}
          <div className="cta-buttons" style={{ marginTop: 14 }}>
            <a className="btn ghost" href={WINDOWS_INSTALLER_URL}>
              ⬇ Windows POS
            </a>
            <a className="btn ghost" href={ANDROID_PLAY_URL} target="_blank" rel="noreferrer">
              ▶ Android POS on Google Play
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      {/* Contact — rendered only when there is something real to show. */}
      {HAS_CONTACT && (
        <section id="contact" className="landing-section contact-section">
          <div className="landing-container">
            <div className="section-head text-center">
              <span className="section-eyebrow">TALK TO US</span>
              <h2>Questions Before You Start?</h2>
              <p>
                A real person answers. Tell us about your counter and we will tell you
                honestly whether Billdedo fits it.
              </p>
            </div>

            <div className="contact-grid">
              {CONTACT.whatsapp && (
                <a
                  className="contact-card"
                  href={`https://wa.me/${CONTACT.whatsapp}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <span className="contact-icon" aria-hidden>💬</span>
                  <strong>WhatsApp</strong>
                  <span className="hint">Fastest reply, most days</span>
                </a>
              )}
              {CONTACT.phones.length > 0 && (
                /* A div, not a link: with more than one number the card
                   cannot itself be the target, and an anchor inside an
                   anchor is not valid markup. Each number is its own. */
                <div className="contact-card">
                  <span className="contact-icon" aria-hidden>📞</span>
                  {CONTACT.phones.map((number) => (
                    <a key={number} className="contact-phone" href={`tel:${number.replace(/\s/g, '')}`}>
                      {number}
                    </a>
                  ))}
                  <span className="hint">Call the team</span>
                </div>
              )}
              {CONTACT.email && (
                <a className="contact-card" href={`mailto:${CONTACT.email}`}>
                  <span className="contact-icon" aria-hidden>✉️</span>
                  <strong>{CONTACT.email}</strong>
                  <span className="hint">Email us anything</span>
                </a>
              )}
              {CONTACT.address && (
                <div className="contact-card">
                  <span className="contact-icon" aria-hidden>📍</span>
                  <strong>Where we are</strong>
                  <span className="hint">{CONTACT.address}</span>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      <footer className="landing-footer">
        <div className="landing-container footer-inner">
          <div className="footer-left">
            <strong>BILLDEDO</strong>
            <span className="hint">Restaurant billing that belongs to you</span>
          </div>
          <div className="footer-links">
            <a href="/portal" onClick={(e) => { e.preventDefault(); onLogin(); }}>
              {signedIn ? 'Console' : 'Log In'}
            </a>
            <a href="#pricing">Pricing</a>
            <a href="#features">Features</a>
            <a href="#private-cloud">Your Data</a>
            {HAS_CONTACT && <a href="#contact">Contact</a>}
          </div>
          <div className="footer-copy hint">
            © {new Date().getFullYear()} BILLDEDO · Restaurant billing, kitchen screens and table service in one place
          </div>
        </div>
      </footer>
    </div>
  );
}
